/* Wishlist (phase 1: guest / localStorage)
   - Heart buttons: [data-wishlist-toggle] with data-product-id + data-product-handle
   - Header count: [data-wishlist-count]
   - Wishlist page: [data-wishlist-page] wrapper with [data-wishlist-grid] and [data-wishlist-empty]

   Phase 2 (account sync): when the customer is logged in, the guest list is
   merged to the server (Shopify App Proxy -> AWS Lambda -> DynamoDB) once per
   page load, and the server list is mirrored into localStorage. All UI reads
   stay synchronous against the mirror; adds/removes sync in the background.
   If the proxy is not configured or any call fails, guest mode continues. */
(function () {
  var script = document.currentScript;
  var CONFIG = {
    storageKey: 'nyc-wishlist',
    maxItems: 40,
    limitMessage: 'You have reached the limit of 40 wishlist items. Remove an item to add a new one.',
    proxyBase: '/apps/wishlist',
    loggedIn: !!(script && script.getAttribute('data-customer-logged-in') === 'true')
  };
  var synced = false; /* true once the server copy is authoritative */

  var LocalStore = {
    getAll: function () {
      try {
        var list = JSON.parse(window.localStorage.getItem(CONFIG.storageKey) || '[]');
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    },
    saveAll: function (list) {
      try {
        window.localStorage.setItem(CONFIG.storageKey, JSON.stringify(list));
      } catch (e) {
        /* storage unavailable — wishlist is a no-op */
      }
    },
    has: function (id) {
      return this.getAll().some(function (i) {
        return String(i.id) === String(id);
      });
    },
    add: function (item) {
      var list = this.getAll();
      if (this.has(item.id)) return true;
      var cap = synced ? 250 : CONFIG.maxItems;
      if (list.length >= cap) return false;
      list.unshift(item);
      this.saveAll(list);
      return true;
    },
    remove: function (id) {
      this.saveAll(this.getAll().filter(function (i) {
        return String(i.id) !== String(id);
      }));
    },
    count: function () {
      return this.getAll().length;
    }
  };

  var store = LocalStore;

  /* ---- Account sync (phase 2) ---- */

  function remote(path, payload) {
    return fetch(CONFIG.proxyBase + path, {
      method: payload === undefined ? 'GET' : 'POST',
      headers: payload === undefined ? {} : { 'Content-Type': 'application/json' },
      body: payload === undefined ? undefined : JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error('wishlist sync ' + r.status);
      return r.json();
    });
  }

  function syncFromServer() {
    if (!CONFIG.loggedIn) return Promise.resolve(false);
    /* merge pushes any guest/offline items and returns the authoritative list */
    return remote('/merge', { items: store.getAll() })
      .then(function (data) {
        if (!data || !Array.isArray(data.items)) return false;
        store.saveAll(data.items);
        synced = true;
        return true;
      })
      .catch(function () {
        return false; /* proxy absent or backend down: stay in guest mode */
      });
  }

  function remoteAdd(item) {
    if (!synced) return;
    remote('/items', { id: item.id, handle: item.handle }).catch(function () {});
  }

  function remoteRemove(id) {
    if (!synced) return;
    remote('/remove', { id: id }).catch(function () {});
  }

  function updateButtons() {
    var buttons = document.querySelectorAll('[data-wishlist-toggle]');
    if (!buttons.length) return;
    /* parse storage once per pass, not once per button */
    var ids = {};
    var list = store.getAll();
    for (var j = 0; j < list.length; j++) ids[String(list[j].id)] = true;
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var active = !!ids[btn.getAttribute('data-product-id')];
      if (btn.classList.contains('is-active') !== active) {
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      }
    }
  }

  function updateCounts() {
    var count = store.count();
    var text = String(count);
    var els = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < els.length; i++) {
      /* only write on change to avoid MutationObserver feedback loops */
      if (els[i].textContent !== text) els[i].textContent = text;
      var shouldHide = count === 0;
      if (els[i].hidden !== shouldHide) els[i].hidden = shouldHide;
    }
  }

  function refresh() {
    updateButtons();
    updateCounts();
    document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { count: store.count() } }));
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    var id = btn.getAttribute('data-product-id');

    if (store.has(id)) {
      store.remove(id);
      remoteRemove(id);
    } else {
      var item = { id: id, handle: btn.getAttribute('data-product-handle'), addedAt: Date.now() };
      if (!store.add(item)) {
        window.alert(CONFIG.limitMessage);
        return;
      }
      remoteAdd(item);
    }

    refresh();
    renderWishlistPage();
  });

  /* ---- Wishlist page ---- */

  function handleize(str) {
    return String(str).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function fetchBadges(handle) {
    return fetch('/products/' + handle + '?view=badges-json')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function refreshCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var els = document.querySelectorAll('[data-js-cart-count]');
        for (var i = 0; i < els.length; i++) els[i].textContent = cart.item_count;
      })
      .catch(function () {});
  }

  function imageUrl(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  var HEART_PATH = 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z';

  function buildCard(product, formatPrice, opts) {
    opts = opts || { remove: true };
    var card = document.createElement('div');
    card.className = 'wishlist-card';

    var imgWrap = document.createElement('div');
    imgWrap.className = 'wishlist-card__image';

    var link = document.createElement('a');
    link.className = 'wishlist-card__link';
    link.href = product.url;
    if (product.featured_image) {
      var img = document.createElement('img');
      img.src = imageUrl(product.featured_image, 600);
      img.alt = product.title;
      img.loading = 'lazy';
      link.appendChild(img);
    }
    imgWrap.appendChild(link);

    if (opts.heart) {
      var heart = document.createElement('button');
      heart.type = 'button';
      heart.className = 'wishlist-heart wishlist-card__heart';
      heart.setAttribute('data-wishlist-toggle', '');
      heart.setAttribute('data-product-id', product.id);
      heart.setAttribute('data-product-handle', product.handle);
      heart.setAttribute('aria-label', 'Add ' + product.title + ' to wishlist');
      heart.setAttribute('aria-pressed', 'false');
      heart.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="' + HEART_PATH + '"/></svg>';
      imgWrap.appendChild(heart);
    }

    if (Array.isArray(product.wishlistBadges) && product.wishlistBadges.length) {
      var badges = document.createElement('div');
      badges.className = 'wishlist-card__badges';
      product.wishlistBadges.forEach(function (label) {
        var b = document.createElement('div');
        b.className = 'product-badge product-badge--custom product-badge--' + handleize(label);
        b.textContent = label;
        badges.appendChild(b);
      });
      imgWrap.appendChild(badges);
    }

    card.appendChild(imgWrap);

    var title = document.createElement('p');
    title.className = 'wishlist-card__title';
    var titleLink = document.createElement('a');
    titleLink.href = product.url;
    titleLink.textContent = product.title;
    title.appendChild(titleLink);
    card.appendChild(title);

    var price = document.createElement('p');
    price.className = 'wishlist-card__price';
    price.textContent = formatPrice(product.price);
    if (product.compare_at_price && product.compare_at_price > product.price) {
      var compare = document.createElement('s');
      compare.className = 'wishlist-card__compare-price';
      compare.textContent = formatPrice(product.compare_at_price);
      price.appendChild(document.createTextNode(' '));
      price.appendChild(compare);
    }
    card.appendChild(price);

    var availableVariants = (product.variants || []).filter(function (v) { return v.available; });
    if (availableVariants.length === 1) {
      var atc = document.createElement('button');
      atc.type = 'button';
      atc.className = 'wishlist-card__atc';
      atc.textContent = 'Push to Cart';
      atc.addEventListener('click', function () {
        atc.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: availableVariants[0].id, quantity: 1 })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('add failed');
            atc.textContent = 'Added \u2713';
            atc.classList.add('is-added');
            refreshCartCount();
          })
          .catch(function () {
            atc.textContent = 'Push to Cart';
            atc.disabled = false;
          });
      });
      card.appendChild(atc);
    } else if (product.available !== false) {
      var pick = document.createElement('a');
      pick.className = 'wishlist-card__atc';
      pick.href = product.url;
      pick.textContent = 'Push to Cart';
      card.appendChild(pick);
    }

    if (opts.remove) {
      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'wishlist-card__remove';
      remove.setAttribute('data-wishlist-remove', product.id);
      remove.textContent = 'Remove';
      card.appendChild(remove);
    }

    return card;
  }

  function renderListCard(root, products) {
    var card = root.querySelector('[data-wishlist-list-card]');
    if (!card) return;

    var withImages = products.filter(function (p) { return p && p.featured_image; });
    if (!products.filter(Boolean).length) {
      card.hidden = true;
      return;
    }
    card.hidden = false;

    var collage = card.querySelector('[data-wishlist-collage]');
    collage.innerHTML = '';
    for (var i = 0; i < 4; i++) {
      var cell = document.createElement('div');
      cell.className = 'wishlist-list-card__cell' + (i === 0 ? ' wishlist-list-card__cell--main' : '');
      if (withImages[i]) {
        var img = document.createElement('img');
        img.src = imageUrl(withImages[i].featured_image, i === 0 ? 600 : 240);
        img.alt = withImages[i].title;
        img.loading = 'lazy';
        cell.appendChild(img);
      }
      collage.appendChild(cell);
    }

    var count = products.filter(Boolean).length;
    var countEl = card.querySelector('[data-wishlist-list-count]');
    var labelEl = card.querySelector('[data-wishlist-list-count-label]');
    if (countEl) countEl.textContent = String(count);
    if (labelEl) labelEl.textContent = count === 1 ? 'product' : 'products';
  }

  function makePriceFormatter(root) {
    var currency = root.getAttribute('data-currency') || 'USD';
    var locale = document.documentElement.lang || 'en';
    var formatter;
    try {
      formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: currency });
    } catch (e) {
      formatter = { format: function (n) { return currency + ' ' + n.toFixed(2); } };
    }
    return function (cents) { return formatter.format(cents / 100); };
  }

  function fetchProductWithBadges(handle) {
    return Promise.all([
      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; }),
      fetchBadges(handle)
    ]).then(function (results) {
      if (results[0]) results[0].wishlistBadges = results[1];
      return results[0];
    });
  }

  /* ---- Recently viewed page ---- */

  var RV_KEY = 'nyc-previously-viewed-products';

  function renderRecentlyViewedPage() {
    var root = document.querySelector('[data-recently-viewed-page]');
    if (!root) return;

    var grid = root.querySelector('[data-rv-grid]');
    var empty = root.querySelector('[data-rv-empty]');
    var countEl = root.querySelector('[data-rv-count]');

    var list = [];
    try {
      list = JSON.parse(window.localStorage.getItem(RV_KEY) || '[]');
    } catch (e) {}
    /* older entries were plain id strings with no handle; they can't be rendered */
    var items = (Array.isArray(list) ? list : []).filter(function (v) { return v && v.handle; });

    if (countEl) countEl.textContent = String(items.length);

    if (!items.length) {
      grid.innerHTML = '';
      empty.hidden = false;
      return;
    }
    empty.hidden = true;

    var formatPrice = makePriceFormatter(root);

    Promise.all(
      items.map(function (item) { return fetchProductWithBadges(item.handle); })
    ).then(function (products) {
      grid.innerHTML = '';
      var shown = 0;
      products.forEach(function (product) {
        if (!product) return;
        grid.appendChild(buildCard(product, formatPrice, { heart: true }));
        shown++;
      });
      if (countEl) countEl.textContent = String(shown);
      updateButtons();
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-rv-clear]');
    if (!btn) return;
    try { window.localStorage.removeItem(RV_KEY); } catch (err) {}
    renderRecentlyViewedPage();
  });

  function renderWishlistPage() {
    var root = document.querySelector('[data-wishlist-page]');
    if (!root) return;

    var grid = root.querySelector('[data-wishlist-grid]');
    var empty = root.querySelector('[data-wishlist-empty]');
    var listCard = root.querySelector('[data-wishlist-list-card]');
    var list = store.getAll();

    if (!list.length) {
      grid.innerHTML = '';
      empty.hidden = false;
      if (listCard) listCard.hidden = true;
      return;
    }
    empty.hidden = true;

    var formatPrice = makePriceFormatter(root);

    Promise.all(
      list.map(function (item) { return fetchProductWithBadges(item.handle); })
    ).then(function (products) {
      grid.innerHTML = '';
      products.forEach(function (product, i) {
        if (!product) {
          /* product deleted or unpublished — drop it from the list */
          store.remove(list[i].id);
          return;
        }
        grid.appendChild(buildCard(product, formatPrice, { remove: true }));
      });
      renderListCard(root, products);
      updateCounts();
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-remove]');
    if (!btn) return;
    store.remove(btn.getAttribute('data-wishlist-remove'));
    remoteRemove(btn.getAttribute('data-wishlist-remove'));
    refresh();
    renderWishlistPage();
  });

  function init() {
    refresh();
    renderWishlistPage();
    renderRecentlyViewedPage();

    syncFromServer().then(function (didSync) {
      if (didSync) {
        refresh();
        renderWishlistPage();
      }
    });

    /* keep hearts in sync on cards added later — debounced so scroll-time
       DOM churn (lazy images, animations) doesn't trigger constant work */
    var obTimer = null;
    new MutationObserver(function () {
      clearTimeout(obTimer);
      obTimer = setTimeout(updateButtons, 150);
    }).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
